"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkRevokedTerminalsResponse = exports.StorePassKeysResponse = exports.StorePassPublicKey = exports.StorePassClaims = exports.STORE_PASS_MAX_SCOPES = exports.STORE_PASS_LOYALTY_KINDS = exports.StorePassScope = exports.STORE_PASS_SCOPES = exports.STORE_PASS_MAX_RENEW_INTERVAL_SECONDS = exports.STORE_PASS_CLOCK_SKEW_SECONDS = exports.STORE_PASS_ALG = void 0;
const zod_1 = require("zod");
const terminal_js_1 = require("./terminal.js");
/**
 * Store passes (RL-450, ADR-0014 §3) — how re:form.lite.link trusts a till
 * without holding the secret that signs till tokens.
 *
 * Back Office signs each paired till of a Link-owned store a pass with a
 * per-tenant Ed25519 key; Link verifies it with the public keys it pulls on
 * `GET /api/v1/link/config`. The till carries the pass on `sync.store-link`
 * (`StorePass` in `store-link.ts`) and presents it to Link as
 * `Authorization: StorePass <jws>`.
 *
 * The wire form is a compact JWS, `alg: EdDSA`, with the signing key's id in
 * the protected header's `kid`. The claims are below; nothing else is signed
 * into a pass, and a verifier refuses a pass whose claims do not parse.
 */
/** The JWS `alg` every store pass carries. Anything else is refused. */
exports.STORE_PASS_ALG = 'EdDSA';
/**
 * How far past `exp` Link still accepts a pass: its own clock and Back
 * Office's may disagree by this much (ADR-0014 §3, "clock tolerance").
 */
exports.STORE_PASS_CLOCK_SKEW_SECONDS = 300;
/** Renewal cadence ceiling: a till renews at least daily while it is online. */
exports.STORE_PASS_MAX_RENEW_INTERVAL_SECONDS = 24 * 60 * 60;
/**
 * RL-569 — what a store pass grants beyond the store's feeds and uploads.
 *
 * A pass always lets its till read the store's connector feeds and upload its
 * documents (RL-458, RL-460): that is what a pass was for before scopes
 * existed, and passes without `scp` keep exactly that. Anything more is a
 * scope, named in the `scp` claim, and a Link refuses a route that needs a
 * scope the pass does not name — **deny by default**, so a pass Back Office
 * issued before scopes existed never gains a new grant.
 *
 * - `loyalty` — resolve loyalty customers through the Link (card or phone →
 *   member and balances; `link-loyalty.ts`). Personal data, so it is granted
 *   per terminal: Back Office names it only in the pass of a point-of-sale
 *   till (`STORE_PASS_LOYALTY_KINDS`) of a store whose `loyaltySource` is
 *   `link`, and a Link checks both the scope and the kind.
 */
exports.STORE_PASS_SCOPES = ['loyalty'];
exports.StorePassScope = zod_1.z.enum(exports.STORE_PASS_SCOPES);
/** RL-569: the till kinds a `loyalty` scope may be granted to. */
exports.STORE_PASS_LOYALTY_KINDS = ['pos', 'sco'];
/** Most scopes a pass may name; a longer list is not a pass Back Office wrote. */
exports.STORE_PASS_MAX_SCOPES = 16;
exports.StorePassClaims = zod_1.z.object({
    /** Tenant id — the `tenantId` of `LinkConfigResponse`. */
    iss: zod_1.z.string().min(1),
    /** The till's terminal id. */
    sub: zod_1.z.string().min(1),
    /** The till's location id; Link accepts only its own. */
    loc: zod_1.z.string().min(1),
    /** The till's kind. A Link never holds a pass, so `link` is not a value. */
    knd: terminal_js_1.TillKind,
    /** Seconds since the epoch, as JWT. */
    iat: zod_1.z.number().int().nonnegative(),
    exp: zod_1.z.number().int().positive(),
    /** Unique per issued pass. */
    jti: zod_1.z.string().min(1),
    /**
     * RL-569 — the scopes this pass grants (`StorePassScope`), absent when it
     * grants none. A plain string list, not the enum: a verifier built before a
     * scope existed must still accept a pass naming it (and grant nothing
     * for it), or a new scope would lock older Links out of every till.
     * Absent is not "everything": see `STORE_PASS_SCOPES`.
     */
    scp: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(exports.STORE_PASS_MAX_SCOPES).optional(),
});
/**
 * One store-pass verification key, as a public Ed25519 JWK with its key id.
 * `x` is the 32-byte public key, base64url. There is no private part in this
 * shape, and no API ever returns one.
 */
exports.StorePassPublicKey = zod_1.z.object({
    kid: zod_1.z.string().min(1),
    kty: zod_1.z.literal('OKP'),
    crv: zod_1.z.literal('Ed25519'),
    x: zod_1.z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    /**
     * `current` signs new passes; `previous` no longer signs but may still have
     * signed a pass that has not expired, so Link keeps accepting it.
     */
    status: zod_1.z.enum(['current', 'previous']),
    createdAt: zod_1.z.string().datetime(),
    /** When it stopped signing; null for the current key. */
    retiredAt: zod_1.z.string().datetime().nullable(),
});
/**
 * `GET /api/v1/connectors/link/store-pass-keys` and the answer to a rotation:
 * the tenant's store-pass keys as an operator sees them. Public halves only.
 */
exports.StorePassKeysResponse = zod_1.z.object({
    keys: zod_1.z.array(exports.StorePassPublicKey),
});
/**
 * `GET /api/v1/link/revoked-terminals` — the terminal ids whose passes for the
 * Link's location Link must refuse although they still verify: the location's
 * revoked tills, and every till holding an unexpired pass for it that is no
 * longer an active till there (moved to another store or to none, revoked
 * after moving, reset), or all of them once the store no longer uses Link.
 * Link refuses a pass whose `sub` is listed, which cuts such a till off before
 * its last pass expires whenever Link has been online since the change.
 */
exports.LinkRevokedTerminalsResponse = zod_1.z.object({
    locationId: zod_1.z.string(),
    terminalIds: zod_1.z.array(zod_1.z.string()),
    generatedAt: zod_1.z.string().datetime(),
});
//# sourceMappingURL=store-pass.js.map